import { Accounts } from "../Accounts";
import { Ajax } from "../infra/Ajax";
import { Calculations } from "../crud/Calculations";
import { Calculator } from "../Calculator";
import { Files } from "../crud/Files";
import { OAuth2 } from "../infra/OAuth2";
import { Onboardings } from "../Onboardings";
import { Reports } from "../Reports";
import { Session } from "../Session";
import { Taxcards } from "../crud/Taxcards";
import { Test } from "../Test";

/**
 * Client to Salaxy API.
 * Abstracts the raw REST api to async methods that are easier to call from TypeScript / JavaScript.
 * Differnet platforms (NG1, NG, jQuery etc.) create derived Clients typically at least by injecting their own Ajax implementation,
 * potentially also by overriding other methods.
 */
export abstract class Client {

  private _ajax: Ajax;
  private _accountsApi: Accounts;
  private _calculationsApi: Calculations;
  private _calculatorApi: Calculator;
  private _reportsApi: Reports;
  private _filesApi: Files;
  private _sessionApi: Session;
  private _oAuth2Api: OAuth2;
  private _testApi: Test;
  private _taxcardsApi: Taxcards;
  private _onboardingsApi: Onboardings;

  constructor(ajaxImpl: Ajax) {
    this._ajax = ajaxImpl;
  }

  /** The platform (NG1, NG, jQuery etc.) dependent class that performs the actual Ajax calls */
  public get ajax(): Ajax {
    return this._ajax;
  }

  /** Gets the Accounts API: Access to current account, its products and workers */
  public get accountsApi(): Accounts {
    if (!this._accountsApi) {
      this._accountsApi = new Accounts(this._ajax);
    }
    return this._accountsApi;
  }

  /** Gets the Calculations API: CRUD operations on calculations */
  public get calculationsApi(): Calculations {
    if (!this._calculationsApi) {
      this._calculationsApi = new Calculations(this._ajax);
    }
    return this._calculationsApi;
  }

  /** Gets the Calculator API: Anonymous salary calculation methods */
  public get calculatorApi(): Calculator {
    if (!this._calculatorApi) {
      this._calculatorApi = new Calculator(this._ajax);
    }
    return this._calculatorApi;
  }

  /** Gets the Reports API: Viewing and generating reports in different formats */
  public get reportsApi(): Reports {
    if (!this._reportsApi) {
      this._reportsApi = new Reports(this._ajax);
    }
    return this._reportsApi;
  }

  /** Gets the Files API: Files stored by the user, thumbnails of pictures etc. */
  public get filesApi(): Files {
    if (!this._filesApi) {
      this._filesApi = new Files(this._ajax);
    }
    return this._filesApi;
  }

  /** Gets the Session API: Access to current session, user and roles */
  public get sessionApi(): Session {
    if (!this._sessionApi) {
      this._sessionApi = new Session(this._ajax);
    }
    return this._sessionApi;
  }

  /** Gets the OAuth2 API: Authentication methods */
  public get oAuth2Api(): OAuth2 {
    if (!this._oAuth2Api) {
      this._oAuth2Api = new OAuth2(this._ajax);
    }
    return this._oAuth2Api;
  }

  /** Gets the Test API: Testing different functionalities of the API and system in general. */
  public get testApi(): Test {
    if (!this._testApi) {
      this._testApi = new Test(this._ajax);
    }
    return this._testApi;
  }

  /** Gets the Tax cards API */
  public get taxcardsApi(): Taxcards {
    if (!this._taxcardsApi) {
      this._taxcardsApi = new Taxcards(this._ajax);
    }
    return this._taxcardsApi;
  }

  /** Gets the Onboarding API for creating a new user */
  public get onboardingsApi(): Onboardings {
    if (!this._onboardingsApi) {
      this._onboardingsApi = new Onboardings(this._ajax);
    }
    return this._onboardingsApi;
  }
}
